import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  Building2, Users, Target, Award, Leaf, Lightbulb, Shield,
  TrendingUp, Clock, CheckCircle2, ArrowRight
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

const timeline = [
  { year: '2017', title: 'Foundation', description: 'GSM Corporation was established by Mr. Manoj More, entering the government projects and tender ecosystem.' },
  { year: '2018', title: 'Education Focus', description: 'Expanded into smart classroom solutions, school furniture, and educational supplies.' },
  { year: '2019', title: 'Healthcare Entry', description: 'Entered healthcare infrastructure with hospital furniture, medical equipment, and nutrition programs.' },
  { year: '2020', title: 'Digital Transformation', description: 'Launched digital solutions including software development, IT infrastructure, and surveillance systems.' },
  { year: '2021', title: 'Group Expansion', description: 'Established 11 group companies across different verticals and sectors.' },
  { year: '2022', title: 'Partnership Growth', description: 'Formed partnerships with Hindustan Unilever, Godrej Interio, Titan Company, and other leading brands.' },
  { year: '2023', title: '₹150 Crore Milestone', description: 'Achieved ₹150+ Crore business valuation with presence across Maharashtra.' },
  { year: '2024', title: 'Pan-India Vision', description: 'Expanding operations across multiple states with franchise and distribution networks.' },
]

const values = [
  { icon: Lightbulb, title: 'Innovation', description: 'Constantly improving and adapting to new technologies for better solutions.' },
  { icon: Award, title: 'Quality', description: 'Delivering excellence in every project with highest standards of quality.' },
  { icon: Leaf, title: 'Sustainability', description: 'Promoting environmentally responsible practices and eco-friendly products.' },
  { icon: Shield, title: 'Integrity', description: 'Maintaining transparency and ethical business conduct in all operations.' },
  { icon: Users, title: 'Customer Focus', description: 'Prioritizing client satisfaction and building long-term partnerships.' },
]

const leadership = [
  {
    name: 'Mr. Manoj More',
    role: 'Managing Director',
    description: 'Visionary entrepreneur with MBA (Marketing), founded GSM Corporation in 2017 after 4-5 years of diverse business experience. Successfully scaled the business to ₹150 Crores.',
    image: '/images/leadership/manoj-more.jpg',
  },
  {
    name: 'Mr. Gajanan Parandkar',
    role: 'Partner / Director',
    description: 'Experienced professional contributing to strategic growth and operations excellence.',
    image: '/images/leadership/gajanan-parandkar.jpg',
  },
  {
    name: 'Mr. Pratap Gaikwad',
    role: 'Partner / Director',
    description: 'Brings extensive expertise in project management and business development.',
    image: '/images/leadership/pratap-gaikwad.jpg',
  },
  {
    name: 'Mr. Vivek Mumbre',
    role: 'Investment Advisor & Director',
    description: 'Strategic investment advisor driving financial growth and sustainability.',
    image: '/images/leadership/vivek-mumbre.jpg',
  },
  {
    name: 'Mr. Ritesh Jaiswal',
    role: 'Executive Director',
    description: 'Executive leadership focusing on operational excellence and expansion.',
    image: '/images/leadership/ritesh-jaiswal.jpg',
  },
  {
    name: 'Mr. Aniruddha Khopade',
    role: 'Executive Director',
    description: 'Executive director with focus on technology and innovation initiatives.',
    image: '/images/leadership/aniruddha-khopade.jpg',
  },
]

const groupCompanies = [
  { name: 'GSM Corp', focus: 'Core Business Operations' },
  { name: 'GPMM Enterprises', focus: 'Manufacturing & Trading' },
  { name: 'Ved Enterprises', focus: 'Educational Supplies' },
  { name: 'Adhya Enterprises', focus: 'Healthcare Solutions' },
  { name: 'ADMS Technoways', focus: 'Technology Services' },
  { name: 'Enteka Systems Pvt Ltd', focus: 'System Integration' },
  { name: 'Mandate IT Solutions Pvt Ltd', focus: 'IT Infrastructure' },
  { name: 'Aarambh Enterprises', focus: 'Startup Initiatives' },
  { name: 'VSM Enterprises', focus: 'Distribution Network' },
  { name: 'Eonixa Dynamics Pvt Ltd', focus: 'Innovation & Technology' },
  { name: 'AHAD Solutions Pvt Ltd', focus: 'Specialized Solutions' },
]

const partners = [
  { name: 'Hindustan Unilever', category: 'FMCG Distribution', description: 'Authorized distributor in Pune & Maharashtra' },
  { name: 'Godrej Interio', category: 'Furniture', description: 'Premium furniture solutions partner' },
  { name: 'Titan Company', category: 'Watches & Jewellery', description: 'World of Titan and Helios brands' },
  { name: 'Mia by Tanishq', category: 'Jewellery', description: 'Diamond and Gold Jewellery brand' },
  { name: 'Blackberrys', category: 'Fashion', description: "Men's apparel and lifestyle retail" },
  { name: 'TITAN IRTH', category: 'Fashion', description: "Women's stylish and premium bags" },
  { name: 'Frozen Bottle', category: 'Food & Beverage', description: 'Premium milkshakes & desserts franchise' },
  { name: 'Momo Panda', category: 'Food & Beverage', description: 'Authentic momo and fast-food chain' },
]

export default function About() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-900/50 to-transparent" />
        <div className="container-premium relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
              <Building2 className="w-4 h-4 text-primary-400" />
              <span className="text-sm text-graphite-300">About GSM Corporation</span>
            </div>
            <h1 className="heading-xl text-white mb-6">
              Building <span className="gradient-text">Tomorrow's</span> Institutions
            </h1>
            <p className="text-xl text-graphite-300 leading-relaxed">
              A dynamic and rapidly growing organization specializing in comprehensive solutions
              for government and institutional projects since 2017.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="section-padding">
        <div className="container-premium">
          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card variant="premium" className="p-8 h-full">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">Our Vision</h2>
                <p className="text-graphite-300 text-lg leading-relaxed">
                  To become a leading provider of sustainable and innovative solutions, setting
                  benchmarks in quality, reliability, and technological advancement across sectors
                  such as education, infrastructure, healthcare, and agriculture.
                </p>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card variant="premium" className="p-8 h-full">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-accent-500 to-primary-500 flex items-center justify-center mb-6">
                  <TrendingUp className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">Our Mission</h2>
                <ul className="space-y-3 text-graphite-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                    <span>Deliver high-quality and cost-effective solutions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                    <span>Support government initiatives and public welfare programs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                    <span>Promote eco-friendly and sustainable products</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                    <span>Build long-term relationships through trust and performance</span>
                  </li>
                </ul>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-padding bg-gradient-to-b from-graphite-950 via-primary-950/10 to-graphite-950">
        <div className="container-premium">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="heading-lg text-white mb-4"
            >
              Our <span className="gradient-text">Journey</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-xl text-graphite-400"
            >
              From humble beginnings to ₹150+ Crore business ecosystem
            </motion.p>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-primary-500 via-secondary-500 to-accent-500" />

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <motion.div
                  key={item.year}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex items-center gap-8 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'text-right' : 'text-left'}`}>
                    <Card variant="glass" className="p-6 inline-block">
                      <div className="text-primary-400 font-bold text-2xl mb-2">{item.year}</div>
                      <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                      <p className="text-graphite-400">{item.description}</p>
                    </Card>
                  </div>
                  <div className="w-4 h-4 rounded-full bg-primary-500 ring-4 ring-primary-500/20 flex-shrink-0 z-10" />
                  <div className="flex-1" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section id="leadership" className="section-padding">
        <div className="container-premium">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-500/10 border border-accent-500/20 mb-6"
            >
              <Users className="w-4 h-4 text-accent-400" />
              <span className="text-accent-400 text-sm font-medium">Leadership Team</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="heading-lg text-white mb-4"
            >
              Meet Our <span className="gradient-text-gold">Leaders</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-xl text-graphite-400 max-w-3xl mx-auto"
            >
              Experienced professionals driving GSM Corporation's growth and excellence
            </motion.p>
          </div>

          {/* Chairman Message */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <Card variant="premium" className="p-8 lg:p-12">
              <div className="grid lg:grid-cols-3 gap-8 items-center">
                <div className="lg:col-span-1">
                  <div className="aspect-square rounded-2xl bg-gradient-to-br from-primary-500/30 via-secondary-500/30 to-accent-500/30 flex items-center justify-center">
                    <Users className="w-24 h-24 text-white/50" />
                  </div>
                </div>
                <div className="lg:col-span-2">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-accent-500 to-primary-500 flex items-center justify-center">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">Mr. Manoj More</h3>
                      <p className="text-accent-400">Managing Director</p>
                    </div>
                  </div>
                  <p className="text-graphite-300 mb-4 leading-relaxed">
                    Visionary entrepreneur with MBA (Marketing) and remarkable journey of perseverance and success.
                    After 4-5 years of diverse business experience, he founded GSM Corporation in 2017, entering the
                    government projects and tender ecosystem.
                  </p>
                  <p className="text-graphite-300 mb-4 leading-relaxed">
                    Through consistent efforts and strategic expansion, he has successfully scaled the business to
                    <span className="text-accent-400 font-semibold"> ₹150+ Crores</span>. Under his leadership, GSM
                    Corporation has built strong partnerships with Hindustan Unilever, Godrej Interio, and Titan Company.
                  </p>
                  <div className="flex items-center gap-4 mt-6">
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-primary-400" />
                      <span className="text-graphite-400">Since 2017</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-accent-400" />
                      <span className="text-graphite-400">₹150+ Crore Business</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Other Leaders */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {leadership.slice(1).map((leader, index) => (
              <motion.div
                key={leader.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card variant="hover" className="p-6 h-full">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary-500/30 to-secondary-500/30 flex items-center justify-center">
                      <Users className="w-8 h-8 text-white/70" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">{leader.name}</h3>
                      <p className="text-sm text-primary-400">{leader.role}</p>
                    </div>
                  </div>
                  <p className="text-graphite-400 text-sm">{leader.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Group Companies */}
      <section className="section-padding bg-gradient-to-b from-graphite-950 via-secondary-950/20 to-graphite-950">
        <div className="container-premium">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="heading-lg text-white mb-4"
            >
              Group <span className="gradient-text-gold">Companies</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-graphite-400"
            >
              11 companies driving growth across diverse sectors
            </motion.p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {groupCompanies.map((company, index) => (
              <motion.div
                key={company.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="glass-card p-4 text-center hover:border-primary-500/30 transition-all group cursor-pointer">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center">
                    <span className="text-white font-bold text-sm">
                      {company.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
                    </span>
                  </div>
                  <h3 className="text-sm font-semibold text-white group-hover:text-primary-400 transition-colors">
                    {company.name}
                  </h3>
                  <p className="text-xs text-graphite-500 mt-1">{company.focus}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners & Brands */}
      <section className="section-padding">
        <div className="container-premium">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="heading-lg text-white mb-4"
            >
              Our <span className="gradient-text">Partners & Brands</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-graphite-400"
            >
              Trusted partnerships with India's leading brands
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {partners.map((partner, index) => (
              <motion.div
                key={partner.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card variant="hover" className="p-6 h-full">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-gradient-to-br from-primary-500/20 to-secondary-500/20 flex items-center justify-center">
                      <Building2 className="w-8 h-8 text-primary-400" />
                    </div>
                    <h3 className="text-white font-semibold mb-1">{partner.name}</h3>
                    <p className="text-primary-400 text-sm mb-2">{partner.category}</p>
                    <p className="text-graphite-500 text-xs">{partner.description}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section id="values" className="section-padding bg-gradient-to-b from-graphite-950 via-primary-950/10 to-graphite-950">
        <div className="container-premium">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="heading-lg text-white mb-4"
            >
              Our Core <span className="gradient-text-cyan">Values</span>
            </motion.h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card variant="hover" className="p-6 h-full text-center">
                  <value.icon className="w-10 h-10 mx-auto text-primary-400 mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">{value.title}</h3>
                  <p className="text-graphite-400 text-sm">{value.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding">
        <div className="container-premium">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-lg text-white mb-6">
                Why Choose <span className="gradient-text">GSM Corporation?</span>
              </h2>
              <p className="text-xl text-graphite-300 mb-8">
                Trusted partner for government and institutional solutions, combining
                expertise, innovation, and reliability.
              </p>

              <div className="space-y-4">
                {[
                  'Proven experience in government tenders & projects',
                  'Wide range of multi-sector solutions under one roof',
                  'Strong vendor and partner network',
                  '₹150+ Crore business ecosystem',
                  '11 group companies across sectors',
                  'Commitment to quality, compliance, and timely delivery',
                  'Focus on innovation and sustainability',
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle2 className="w-5 h-5 text-accent-400 flex-shrink-0" />
                    <span className="text-graphite-300">{item}</span>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8">
                <Button variant="primary" asChild>
                  <Link to="/contact">
                    Partner With Us
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              {[
                { value: '₹150+', label: 'Crore Business' },
                { value: '11', label: 'Group Companies' },
                { value: '50+', label: 'Districts Covered' },
                { value: '7+', label: 'Years Experience' },
                { value: '500+', label: 'Happy Clients' },
                { value: '100+', label: 'Projects Delivered' },
              ].map((stat, index) => (
                <Card key={index} variant="glass" className="p-6 text-center">
                  <div className="stats-number">{stat.value}</div>
                  <div className="text-graphite-400 mt-2">{stat.label}</div>
                </Card>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}